import type { ShapeKind } from '../../shared/types.ts';
import type { Editor } from '../editor.ts';
import type { ToolName } from '../tools/types.ts';
import { h } from '../util/dom.ts';
import { icons } from './icons.ts';

interface ToolButton { tool: ToolName; kind?: ShapeKind; label: string; key: string; icon: string }

const BUTTONS: ToolButton[] = [
  { tool: 'select', label: 'Select', key: 'V', icon: icons.select },
  { tool: 'hand', label: 'Hand', key: 'H', icon: icons.hand },
  { tool: 'sticky', label: 'Sticky note', key: 'N', icon: icons.sticky },
  { tool: 'card', label: 'Card', key: 'C', icon: icons.card },
  { tool: 'text', label: 'Text', key: 'T', icon: icons.text },
  { tool: 'shape', kind: 'rect', label: 'Rectangle', key: 'R', icon: icons.rect },
  { tool: 'shape', kind: 'ellipse', label: 'Ellipse', key: 'O', icon: icons.ellipse },
  { tool: 'shape', kind: 'diamond', label: 'Diamond', key: '', icon: icons.diamond },
  { tool: 'shape', kind: 'triangle', label: 'Triangle', key: '', icon: icons.triangle },
  { tool: 'connector', label: 'Connector', key: 'L', icon: icons.connector },
  { tool: 'pen', label: 'Pen', key: 'P', icon: icons.pen },
  { tool: 'eraser', label: 'Eraser', key: 'E', icon: icons.eraser },
  { tool: 'frame', label: 'Frame', key: 'F', icon: icons.frame },
];

function button(label: string, icon: string, onClick: () => void): HTMLButtonElement {
  const b = h('button', { type: 'button', class: 'tb-btn', title: label, 'aria-label': label });
  b.innerHTML = icon;
  b.addEventListener('click', onClick);
  return b;
}

export function createToolbar(ed: Editor): HTMLElement {
  const bar = h('div', { class: 'panel toolbar', role: 'toolbar', 'aria-label': 'Tools', 'aria-orientation': 'vertical' });

  const available = ed.readOnly ? BUTTONS.filter((t) => t.tool === 'select' || t.tool === 'hand') : BUTTONS;
  const toolButtons = available.map((t) => {
    const title = t.key ? `${t.label} (${t.key})` : t.label;
    const b = button(title, t.icon, () => {
      if (t.kind) ed.shapeKind = t.kind;
      ed.setTool(t.tool);
      sync();
    });
    return { b, t };
  });
  const undo = button('Undo (⌘Z)', icons.undo, () => ed.undo());
  const redo = button('Redo (⇧⌘Z)', icons.redo, () => ed.redo());

  bar.append(...toolButtons.map((x) => x.b));
  if (!ed.readOnly) {
    if (!ed.local) {
      bar.append(button('Insert image or audio', icons.image, () => ed.pickFiles()));
      if (ed.recordAudio) bar.append(button('Record audio', icons.mic, () => ed.recordAudio?.()));
    }
    bar.append(h('div', { class: 'tb-sep' }), undo, redo);
  }

  const sync = () => {
    for (const { b, t } of toolButtons) {
      const on = ed.toolName === t.tool && (!t.kind || ed.shapeKind === t.kind);
      b.classList.toggle('active', on);
      b.setAttribute('aria-pressed', String(on));
    }
    undo.disabled = !ed.history.canUndo;
    redo.disabled = !ed.history.canRedo;
  };
  ed.events.on('tool', sync);
  ed.events.on('history', sync);
  sync();
  return bar;
}

export function createZoomControls(ed: Editor): HTMLElement {
  const out = h('button', { type: 'button', class: 'tb-btn', title: 'Zoom out (⌘−)', 'aria-label': 'Zoom out' }, ['−']);
  const level = h('button', { type: 'button', class: 'zoom-level', title: 'Zoom to fit (⇧1)' });
  const inn = h('button', { type: 'button', class: 'tb-btn', title: 'Zoom in (⌘+)', 'aria-label': 'Zoom in' }, ['+']);
  out.addEventListener('click', () => ed.zoomBy(0.8));
  inn.addEventListener('click', () => ed.zoomBy(1.25));
  level.addEventListener('click', () => ed.zoomToFit());

  let shown = '';
  const sync = () => {
    const text = `${Math.round(ed.camera.zoom * 100)}%`;
    if (text !== shown) level.textContent = shown = text;
  };
  ed.events.on('camera', sync);
  sync();
  return h('div', { class: 'panel zoom' }, [out, level, inn]);
}
